/* eslint-disable @typescript-eslint/explicit-function-return-type */
/* eslint-disable consistent-return */
// inspired from Tabs/TabIndicator.web.tsx
import React from 'react';
import styled from 'styled-components';
import { castWebType, makeBorderSize, makeMotionTime, makeSpace } from '~utils';
import { size } from '~tokens/global';
import { metaAttribute, MetaConstants } from '~utils/metaAttribute';
import { useTheme } from '~components/BladeProvider';
import { useResize } from '~utils/useResize';
import { useIsomorphicLayoutEffect } from '~utils/useIsomorphicLayoutEffect';
import BaseBox from '~components/Box/BaseBox';

const StyledIndicatorWrapper = styled.div({
  pointerEvents: 'none',
  position: 'absolute',
  bottom: 0,
  left: 0,
  willChange: 'transform, width, opacity',
});

const StyledTabNavIndicatorLine = styled(BaseBox)(({ theme }) => {
  return {
    position: 'relative',
    width: '100%',
    height: makeSpace(theme.border.width.thinner),
    borderTopLeftRadius: makeBorderSize(theme.border.radius.medium),
    borderTopRightRadius: makeBorderSize(theme.border.radius.medium),
  };
});

const GLOW_OVERFLOW = 32;
const GLOW_HEIGHT = size[56];

const buildGlowMask = (width: number, height: number = GLOW_HEIGHT): string => {
  const cx = width / 2;
  const rx = width * 0.37;
  return `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='${width}' height='${height}'%3E%3Cdefs%3E%3Cfilter id='b' filterUnits='userSpaceOnUse' x='0' y='0' width='${width}' height='${height}'%3E%3CfeGaussianBlur stdDeviation='40'/%3E%3C/filter%3E%3C/defs%3E%3Cellipse cx='${cx}' cy='${height}' rx='${rx}' ry='60' fill='black' filter='url(%23b)'/%3E%3C/svg%3E")`;
};

const StyledIndicatorGlow = styled.div<{
  glowColor: string;
  glowWidth: number;
  glowMask: string;
}>(({ glowColor, glowWidth, glowMask }) => {
  const totalWidth = glowWidth + GLOW_OVERFLOW * 2;
  return {
    position: 'absolute',
    bottom: 0,
    left: `${-GLOW_OVERFLOW}px`,
    width: `${totalWidth}px`,
    height: `${GLOW_HEIGHT}px`,
    // Tuned visually to blend the glow into the dark TopNav background without overpowering the indicator line
    opacity: 0.8,
    background: `radial-gradient(50% 100% at 50% 100%, ${glowColor} 0%, transparent 100%)`,
    WebkitMaskImage: glowMask,
    WebkitMaskRepeat: 'no-repeat',
    WebkitMaskPosition: 'center bottom',
    WebkitMaskSize: '100% 100%',
    maskImage: glowMask,
    maskRepeat: 'no-repeat',
    maskPosition: 'center bottom',
    maskSize: '100% 100%',
  };
});

const ACTIVE_ITEM_SELECTOR = '[data-blade-component="tab-nav-item"][data-active="true"]';

const TabNavIndicator = ({
  containerRef,
}: {
  containerRef: React.RefObject<HTMLElement | null>;
}): React.ReactElement => {
  const { theme } = useTheme();
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const shouldAnimateRef = React.useRef(false);
  const [activeWidth, setActiveWidth] = React.useState(0);
  const [glowColor, setGlowColor] = React.useState(theme.colors.surface.background.primary.intense);

  const glowMask = React.useMemo(() => buildGlowMask(activeWidth + GLOW_OVERFLOW * 2), [
    activeWidth,
  ]);

  const updatePosition = React.useCallback(() => {
    const container = containerRef.current;
    const wrapper = wrapperRef.current;
    if (!container || !wrapper) return;

    const activeItem = container.querySelector<HTMLElement>(ACTIVE_ITEM_SELECTOR);

    if (!activeItem || activeItem.offsetWidth === 0) {
      wrapper.style.opacity = '0';
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const activeRect = activeItem.getBoundingClientRect();
    const x = activeRect.left - containerRect.left;

    const duration = shouldAnimateRef.current
      ? makeMotionTime(theme.motion.duration.gentle)
      : '0ms';

    wrapper.style.transitionDuration = castWebType(duration);
    wrapper.style.width = `${activeRect.width}px`;
    wrapper.style.transform = `translateX(${x}px)`;
    wrapper.style.opacity = '1';

    setActiveWidth(activeRect.width);
    setGlowColor(activeItem.dataset.glowColor ?? theme.colors.surface.background.primary.intense);

    if (!shouldAnimateRef.current) {
      requestAnimationFrame(() => {
        shouldAnimateRef.current = true;
      });
    }
  }, [containerRef, theme.motion.duration.gentle, theme.colors.surface.background.primary.intense]);

  useIsomorphicLayoutEffect(() => {
    void updatePosition();
  }, [updatePosition]);

  // Watch for data-active attribute changes AND child additions/removals so the
  // indicator moves when: (a) active tab changes via router, (b) an item moves
  // into/out of the "More" overflow (the More button is added to the DOM with
  // data-active already set, so a pure attribute observer would miss it).
  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let rafId: number;
    const observer = new MutationObserver(() => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        void updatePosition();
      });
    });

    observer.observe(container, {
      attributes: true,
      attributeFilter: ['data-active'],
      childList: true,
      subtree: true,
    });

    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [containerRef, updatePosition]);

  React.useEffect(() => {
    if ('fonts' in document) {
      try {
        void document.fonts.ready.then(() => {
          updatePosition();
        });
      } catch {
        /* empty */
      }
    }
  }, [updatePosition]);

  useResize(containerRef, updatePosition);

  return (
    <StyledIndicatorWrapper
      ref={wrapperRef}
      style={{
        transitionProperty: 'transform, width, opacity',
        transitionTimingFunction: castWebType(theme.motion.easing.standard),
        transitionDuration: '0ms',
        opacity: 0,
      }}
      {...metaAttribute({ name: MetaConstants.TabNavIndicator })}
    >
      {activeWidth > 0 && (
        <StyledIndicatorGlow glowColor={glowColor} glowWidth={activeWidth} glowMask={glowMask} />
      )}
      <StyledTabNavIndicatorLine
        style={{
          background: `linear-gradient(90deg, transparent 0%, ${theme.colors.surface.icon.staticWhite.normal} 20%, ${theme.colors.surface.icon.staticWhite.normal} 80.29%, transparent 100%)`,
        }}
      />
    </StyledIndicatorWrapper>
  );
};

export { TabNavIndicator };
